import { gql, useMutation, useQuery } from "@apollo/client";
import {
  FullFocusDialog,
  FullFocusDialogContent,
  PrimaryFullScreenSpinner,
  toast,
} from "@repo/ui";
import { currencySchema } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Duration } from "luxon";
import { Suspense, useEffect } from "react";
import { z } from "zod";
import logoBlueOnWhite from "~/assets/logoBlueOnWhite.svg";
import { NewTermDepositPage } from "~/components/TermDeposits/new-term-deposit-page";
import { opsHead } from "~/head";

const paramsSchema = z.object({
  businessEntityId: z.string(),
  currency: currencySchema,
  rolloverFrom: z.string().nullish(),
});

const newTermDepositPageQuery = gql`
  query AdminNewTermDepositPage($businessEntityId: ID!) {
    node(id: $businessEntityId) {
      id
      ... on BusinessEntity {
        id
        name
        organisation {
          id
          name
        }
        assets(includeInactive: true) {
          __typename
          id
          name
          currency
          status
          providerName
          estimatedValue {
            amount
            currency
          }
          ... on TermDepositAsset {
            apy
            start
            end
          }
        }
      }
    }
    products {
      id
      name
      ... on TermDepositProduct {
        provider {
          id
          name
        }
        rateQuotes {
          id
          duration
          rate
        }
      }
    }
  }
`;

type QueryVars = {
  businessEntityId: string;
};

const createTermDepositMutation = gql`
  mutation AdminCreateTermDeposit($input: CreateTermDepositInput!) {
    createTermDeposit(input: $input) {
      termDepositAsset {
        id
      }
      rolloverFromAsset {
        id
      }
    }
  }
`;

export const Route = createFileRoute("/__auth/term-deposits-queue_/new")({
  head: () => opsHead("New Term Deposit"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const [createTermDeposit] = useMutation(createTermDepositMutation);

  const parsedSearchParams = paramsSchema.safeParse({
    businessEntityId: searchParams.get("businessEntityId"),
    currency: searchParams.get("currency"),
    rolloverFrom: searchParams.get("rolloverFrom"),
  });

  const businessEntityId = parsedSearchParams.success
    ? parsedSearchParams.data.businessEntityId
    : "";
  const currency = parsedSearchParams.success
    ? parsedSearchParams.data.currency
    : "AUD";
  const rolloverFrom = parsedSearchParams.success
    ? parsedSearchParams.data.rolloverFrom
    : undefined;

  const { data, loading } = useQuery<any, QueryVars>(newTermDepositPageQuery, {
    variables: {
      businessEntityId,
    },
    skip: !parsedSearchParams.success,
  });

  useEffect(() => {
    if (!parsedSearchParams.success) {
      void navigate({ to: "/term-deposits-queue" });
    }
  }, [navigate, parsedSearchParams.success]);

  useEffect(() => {
    if (
      parsedSearchParams.success &&
      !loading &&
      data?.node?.__typename !== "BusinessEntity"
    ) {
      void navigate({ to: "/term-deposits-queue" });
    }
  }, [data?.node?.__typename, loading, navigate, parsedSearchParams.success]);

  if (
    !parsedSearchParams.success ||
    loading ||
    data?.node?.__typename !== "BusinessEntity"
  ) {
    return null;
  }

  const businessEntity = data.node as {
    id: string;
    name: string;
    organisation?: { id: string; name: string | null } | null;
    assets?: any[] | null;
  };

  const quotes = ((data?.products ?? []) as any[])
    .filter((product: any) => product?.__typename === "TermDepositProduct")
    .flatMap((product) =>
      (product.rateQuotes ?? []).map((rateQuote: any) => ({
        id: product.id,
        providerName: product.provider?.name ?? "",
        durationDays:
          typeof rateQuote.duration === "string"
            ? Duration.fromISO(rateQuote.duration).as("days")
            : rateQuote.duration.as("days"),
        apy: rateQuote.rate,
      })),
    );

  const sourceAssets = (businessEntity.assets ?? []).map((asset: any) => ({
    id: asset.id,
    name: asset.name,
    currency: asset.currency,
    balance: asset.estimatedValue?.amount ?? new Decimal(0),
  }));

  const rolloverFromAsset = rolloverFrom
    ? (businessEntity.assets ?? []).find(
        (asset: any) =>
          asset.id === rolloverFrom && asset.__typename === "TermDepositAsset",
      )
    : undefined;

  let rolloverFromData;

  if (rolloverFromAsset?.__typename === "TermDepositAsset") {
    const providerName = rolloverFromAsset.providerName;

    if (!providerName) {
      throw new Error("Found term deposit with unknown provider");
    }

    rolloverFromData = {
      id: rolloverFromAsset.id,
      name: rolloverFromAsset.name,
      currency: rolloverFromAsset.currency,
      balance: rolloverFromAsset.estimatedValue?.amount ?? new Decimal(0),
      apy: (rolloverFromAsset.apy ?? new Decimal(0)).div(100),
      startUTC: rolloverFromAsset.start,
      endUTC: rolloverFromAsset.end,
      providerName,
    };
  }

  return (
    <FullFocusDialog
      open={true}
      onOpenChange={(open) => {
        !open && void navigate({ to: "/term-deposits-queue" });
      }}
    >
      <FullFocusDialogContent logoBlueOnWhiteSrc={logoBlueOnWhite}>
        <NewTermDepositPage
          className="container mx-auto h-full"
          onCreateTermDeposit={async (formData) => {
            const funding = Object.entries(formData.funding.fundsSource)
              .filter(
                ([, allocation]) => allocation?.checked && allocation.amount,
              )
              .map(([sourceKey, allocation]) => ({
                sourceAssetId: sourceKey === "external" ? null : sourceKey,
                amount: new Decimal(allocation?.amount ?? 0),
              }))
              .filter((entry) => entry.amount.gt(0));

            try {
              const result = await createTermDeposit({
                variables: {
                  input: {
                    organisationId: formData.organisationId,
                    businessEntityId: formData.businessEntityId,
                    productId: formData.quote.quoteId,
                    principalAmount: new Decimal(
                      formData.quote.principalAmount,
                    ),
                    apy: new Decimal(
                      formData.quote.customApy ?? formData.quote.apy,
                    ),
                    durationDays: formData.quote.durationDays,
                    startDate: formData.configuration.startDate ?? null,
                    currency: formData.configuration.currency,
                    rolloverFromAssetId: formData.rolloverFrom?.id ?? null,
                    funding,
                    approvalType: formData.review.approvalType,
                  },
                },
              });

              const rolloverFromId =
                result.data?.createTermDeposit.rolloverFromAsset?.id;
              const createdId =
                result.data?.createTermDeposit.termDepositAsset.id;

              if (!createdId) {
                throw new Error("No term deposit returned");
              }

              toast({
                kind: "success",
                title: rolloverFromId
                  ? "Rolled over term deposit"
                  : "Created new term deposit",
              });
              void navigate({
                to: "/term-deposits-queue.$termDepositId",
                params: { termDepositId: rolloverFromId ?? createdId },
              });
            } catch {
              toast({
                kind: "error",
                title: "Failed to create asset",
              });
            }
            return;
          }}
          businessEntityId={businessEntity.id}
          currency={currency}
          organisationName={businessEntity.organisation?.name ?? "Organisation"}
          organisationId={businessEntity.organisation?.id ?? ""}
          businessEntityName={businessEntity.name}
          getQuotes={({ durationDays, provider }) =>
            quotes
              .filter((quote: { providerName: string }) =>
                provider === undefined ? true : quote.providerName === provider,
              )
              .filter((quote: { durationDays: number }) =>
                durationDays === undefined
                  ? true
                  : quote.durationDays === durationDays,
              )
              .map(
                (quote: {
                  id: string;
                  providerName: string;
                  durationDays: number;
                  apy: Decimal;
                }) => ({
                  ...quote,
                  apy: quote.apy.div(100),
                }),
              )
          }
          sourceAssets={sourceAssets}
          rolloverFrom={rolloverFromData}
        />
      </FullFocusDialogContent>
    </FullFocusDialog>
  );
}
